import { STATUS_LABELS } from '../constants/colors';
import { awaitingApproverLabel } from '../constants/rowStyles';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ONE COPY DECK FOR *SKIPPING A PENDING APPROVAL* — BOTH DOORS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Product owner, 2026-08-19: forcing an order through must *"make the jump
 * demand a reason"*. 2026-09-13: *"the override should require a reason"*, and
 * — on the status dropdown — *"I thought that's what we are fixing now with the
 * requires-a-note to override status"*.
 *
 * 🚨 **THERE ARE TWO DOORS ONTO THE SAME ACT, AND THIS MODULE EXISTS BECAUSE
 * THE USER CANNOT BE EXPECTED TO KNOW THAT.**
 *
 *   1. `Actions ▾ → Next step → Skip remaining approvals…` on Department
 *      Orders → `POST /api/approvals/orders/:id/override`.
 *   2. The `Override order` status dropdown (`components/InlineStatusJump.js`,
 *      the footer of that same `Next step` group) jumped FORWARD past a
 *      pending step → `PATCH /api/orders/:id/status`, which answers
 *      `400 status_jump_reason_required` and then records the skip in the SAME
 *      `approval_override_log` table.
 *
 * They sit two centimetres apart in one panel. Two hand-written wordings would
 * drift within a month and the drift would be INVISIBLE — a dialog wording
 * difference between two controls fails no test and reads as a per-control
 * decision. One deck, imported twice. Same argument, verbatim, as
 * `constants/saveThenActPrompt.js`'s.
 *
 * ── COPY DECISIONS, EACH TAKEN AGAINST AN ALTERNATIVE ─────────────────────
 * - **"Skip remaining approvals", never "Override".** *Override* is the
 *   SERVER'S word for the route and means nothing to the person pressing the
 *   button; the act is *skip every approval step that is still pending*. The
 *   one place the old word survives is the status dropdown's own visible label
 *   `Override order`, which the owner chose himself on 2026-08-12 and which is
 *   not this module's to rename.
 * - **The message NAMES WHO IS BEING SKIPPED**, through the shared
 *   `awaitingApproverLabel` — "Awaiting Jake Dimmer (Buyer)". 🔑 IMPERSONAL,
 *   per the 2026-09-11 chevron ruling, and a step that binds NOBODY prints no
 *   name at all (a NULL `approver_user_id` means the server has explicitly
 *   declined to say whose step it is). That rule is not restated here — it is
 *   the helper's, in one place, which is the whole reason this calls it.
 * - **The message says the requester WILL BE TOLD.** Both routes notify the
 *   order's creator with the reason (2026-09-13). A dialog that collects a
 *   justification without saying who reads it invites a throwaway one.
 * - **The confirm button names the DESTINATION**, because the two doors have
 *   different ones: the menu item always lands on `approved`; the status
 *   dropdown can be jumping to `ordered` or `delivered`. "Confirm" would put
 *   the user back to guessing which control they touched.
 * - 🔴 **NO "skip without a reason" ESCAPE.** `ConfirmDialog` supports a third
 *   button (`altLabel`/`onAlt`) and it is deliberately not offered: the reason
 *   IS the ruling, and a button that opts out of it would undo the ruling
 *   through the UI while the server still refused — the worst of both.
 */

/** The heading. A question, because the dialog is asking. */
export const SKIP_APPROVALS_TITLE = 'Skip remaining approvals?';

/**
 * The textarea's label. `(required)` is stated rather than implied because the
 * Confirm button is disabled until it is non-empty, and an unexplained disabled
 * button reads as a broken dialog.
 */
export const SKIP_APPROVALS_PROMPT_LABEL = 'Reason (required)';

/** What the status dropdown's own wording calls the default destination. */
const DEFAULT_TARGET = 'approved';

const labelFor = (status) => STATUS_LABELS[status] || status;

/**
 * @param {object}  args
 * @param {string}  args.orderNumber      already resolved by the caller
 *                                        (`utils/orderTitle.js#getOrderNumber`)
 *                                        — this module never re-derives it.
 * @param {object} [args.approvalInfo]    the row's `approval_info`, or null.
 * @param {string} [args.targetStatus]    the status being jumped to; defaults
 *                                        to `approved`, which is the only thing
 *                                        the override route can produce.
 * @returns {string}
 */
export function skipApprovalsMessage({ orderNumber, approvalInfo, targetStatus } = {}) {
  const target = labelFor(targetStatus || DEFAULT_TARGET);
  return (
    `${orderNumber || 'This order'} will skip every approval step that is still pending `
    + `and move straight to ${target}. ${awaitingApproverLabel(approvalInfo)}. `
    + 'Your reason is recorded against the order, and the person who raised it is told who skipped it and why.'
  );
}

/**
 * @param {string} [targetStatus] defaults to `approved`.
 * @returns {string} a label that says what the button does, per
 *                   `client/src/pages/CLAUDE.md`'s rule for this dialog family.
 */
export function skipApprovalsConfirmLabel(targetStatus) {
  const status = targetStatus || DEFAULT_TARGET;
  return status === DEFAULT_TARGET
    ? 'Skip approvals and approve'
    : `Skip approvals and set ${labelFor(status)}`;
}
